import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
} from "@mui/material";
import { useState } from "react";
import {
  MoreVert,
  Edit,
  Delete,
  LocationOn,
  Phone,
  Email,
  Schedule,
  WorkOutline,
  Warning,
} from "@mui/icons-material";
import { Shop } from "../../types/shop.types";

interface ShopCardProps {
  shop: Shop;
  onEdit: (shop: Shop) => void;
  onDelete: (shop: Shop) => void;
  onViewHours: (shop: Shop) => void;
  onConfigureCoverage: (shop: Shop) => void;
}

export default function ShopCard({
  shop,
  onEdit,
  onDelete,
  onViewHours,
  onConfigureCoverage,
}: ShopCardProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    onEdit(shop);
    handleMenuClose();
  };

  const handleDelete = () => {
    onDelete(shop);
    handleMenuClose();
  };

  const handleViewHours = () => {
    onViewHours(shop);
    handleMenuClose();
  };

  const handleConfigureCoverage = () => {
    onConfigureCoverage(shop);
    handleMenuClose();
  };

  const getInitials = (name: string, maxChars = 2) =>
    name
      .trim()
      .split(/\s+/)
      .map((word) => word[0])
      .join("")
      .slice(0, maxChars)
      .toUpperCase();

  // Count how many days are open by default
  const openDaysCount = Object.values(shop.defaultHours).filter(
    (hours) => hours.isOpen
  ).length;

  // Check if default hours are set (at least one day has open hours)
  const hasDefaultHours = openDaysCount > 0;

  // Check if default coverage is set (at least one day has coverage requirements)
  const hasDefaultCoverage = shop.defaultCoverage
    ? Object.values(shop.defaultCoverage).some(
        (coverageArray) => coverageArray && coverageArray.length > 0
      )
    : false;

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: 4,
          borderColor: "primary.light",
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        {/* Name and menu */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              sx={{
                bgcolor: shop.color || "primary.light",
                color: "primary.contrastText",
              }}
            >
              {getInitials(shop.name)}
            </Avatar>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              {shop.name}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleMenuOpen}>
            <MoreVert />
          </IconButton>
        </Box>

        {/* Address, Phone, Email */}
        <Box sx={{ display: "grid", gap: 0.75, mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LocationOn
              fontSize="small"
              sx={{ mr: 1, color: "text.disabled" }}
            />
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontStyle: shop.address ? "normal" : "italic",
              }}
            >
              {shop.address || "Not Provided"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Phone fontSize="small" sx={{ mr: 1, color: "text.disabled" }} />
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontStyle: shop.contactInfo?.phone ? "normal" : "italic",
              }}
            >
              {shop.contactInfo?.phone || "Not Provided"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Email fontSize="small" sx={{ mr: 1, color: "text.disabled" }} />
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontStyle: shop.contactInfo?.email ? "normal" : "italic",
              }}
            >
              {shop.contactInfo?.email || "Not Provided"}
            </Typography>
          </Box>
        </Box>

        {/* Status Chips */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2 }}>
          {hasDefaultHours ? (
            <Chip
              icon={<Schedule />}
              label={`Open ${openDaysCount} days/week`}
              size="small"
              color="primary"
              variant="outlined"
            />
          ) : (
            <Chip
              icon={<Warning />}
              label="Set Default Hours"
              size="small"
              color="warning"
              variant="outlined"
              onClick={handleViewHours}
              sx={{
                cursor: "pointer",
                "& .MuiChip-icon": { ml: 1.0 },
              }}
            />
          )}

          {!hasDefaultCoverage && (
            <Chip
              icon={<Warning />}
              label="Set Default Coverage"
              size="small"
              color="warning"
              variant="outlined"
              onClick={handleConfigureCoverage}
              sx={{
                cursor: "pointer",
                "& .MuiChip-icon": { ml: 1.0 },
              }}
            />
          )}
        </Box>
      </CardContent>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewHours}>
          <ListItemIcon>
            <Schedule fontSize="small" />
          </ListItemIcon>
          <ListItemText>Default Hours</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleConfigureCoverage}>
          <ListItemIcon>
            <WorkOutline fontSize="small" />
          </ListItemIcon>
          <ListItemText>Default Coverage</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Shop</ListItemText>
        </MenuItem>
      </Menu>
    </Card>
  );
}
